/**
 * The site's URL map for search engines (ADR-0061, ADR-0062, ADR-0065). Three kinds of path:
 *   - public pages: prerendered to static HTML at build time in each language — English at the root (/programs),
 *     Russian and Spanish under their prefix (/ru/programs, /es/programs) — each with its own title, description,
 *     self canonical and hreflang alternates, all in the sitemap. The English meta is here, the Russian and Spanish in
 *     the dictionaries (i18n/ru/pages.ts, i18n/es/pages.ts: code stays free of Cyrillic);
 *   - app paths: the cabinet, the terminal, the admin, sign-in and the rules page of one program version — served as
 *     the SPA shell with `noindex`, never prefixed (docker/stand/Caddyfile keeps the same list; a unit test compares);
 *   - anything else (an unknown /ru/… too): a real 404.
 * One table for the prerender, the sitemap, llms.txt and the page meta the app sets on client-side navigation, so the
 * HTML a crawler reads and the page after JS agree.
 */
import { DEFAULT_LANG, LANGS, type Lang } from "@prop/i18n";
import ruPages from "../i18n/ru/pages";
import esPages from "../i18n/es/pages";

export const SITE = "https://fundedlime.com";
export const BRAND = "Funded Lime";

export type PageMeta = { title: string; description: string; ogTitle?: string; ogDescription?: string };

/** The legal documents in reading order (the same as i18n/legalDocs.ts LEGAL_DOCS, kept out of the main bundle). */
export const LEGAL_PATHS = ["terms", "refunds", "payouts", "prohibited", "aml", "privacy", "cookies", "risk", "execution"] as const;

export type PublicPath = "/" | "/programs" | "/faq" | `/legal/${(typeof LEGAL_PATHS)[number]}`;

/** Every public page in English: title ≤ 60 characters, description ≤ 155 (src/app/publicRoutes.test.ts, every language). */
export const PUBLIC_PAGES: Readonly<Record<PublicPath, PageMeta>> = {
  "/": {
    title: "Funded Lime — prop trading challenges: crypto, FX, futures",
    description: "Pass a trading challenge on a simulated account with real market data and get funded. Crypto, FX and CME futures, published rules, payouts up to 90%.",
    ogTitle: "Funded Lime — Squeeze the market",
    ogDescription: "Prop trading challenges in crypto, FX and futures on real market data. Published rules, every fill explained, payouts up to 90%.",
  },
  "/programs": {
    title: "Prop trading challenges and prices · Funded Lime",
    description: "Build a challenge: crypto perpetuals, FX/CFD or CME futures; instant, 1-step, 2-step or sprint. Every rule and every price of it, up front.",
  },
  "/faq": {
    title: "Prop trading questions and answers · Funded Lime",
    description: "Is it real money, how orders are filled, when the trading day ends, whether rules can change, what if the data feed fails and how payouts work.",
  },
  "/legal/terms": {
    title: "Terms of Service · Funded Lime",
    description: "The agreement between you and Funded Lime: what we sell, how evaluations and funded accounts work, what is not allowed and what if something goes wrong.",
  },
  "/legal/refunds": {
    title: "Payments and refund policy · Funded Lime",
    description: "How you pay for a Funded Lime program in crypto, what happens when a payment goes wrong and when a fee comes back.",
  },
  "/legal/payouts": {
    title: "Payout policy · Funded Lime",
    description: "When a funded account pays out, how much, where it goes and why a payout request can be refused.",
  },
  "/legal/prohibited": {
    title: "Prohibited trading practices · Funded Lime",
    description: "Trading that exploits the simulation or the rules instead of showing skill, and is not allowed on Funded Lime accounts.",
  },
  "/legal/aml": {
    title: "AML and KYC policy · Funded Lime",
    description: "How Funded Lime keeps the platform away from money laundering, terrorist financing, sanctions evasion and fraud.",
  },
  "/legal/privacy": {
    title: "Privacy policy · Funded Lime",
    description: "What personal data Funded Lime collects, why, who else sees it, how long we keep it and what you can ask of us.",
  },
  "/legal/cookies": {
    title: "Cookie policy · Funded Lime",
    description: "Which cookies and browser storage Funded Lime uses, what for, and how you choose.",
  },
  "/legal/risk": {
    title: "Risk disclosure · Funded Lime",
    description: "Read before you buy: all Funded Lime accounts are simulated. What simulated results mean, and what they do not.",
  },
  "/legal/execution": {
    title: "Execution policy · Funded Lime",
    description: "How the Funded Lime engine fills orders on simulated accounts against real market data: latency, book sweep, spread, fees.",
  },
};

/** The meta of every public page in every language (tsc: each dictionary has every page). */
export const PAGE_METAS: Readonly<Record<Lang, Readonly<Record<PublicPath, PageMeta>>>> = { en: PUBLIC_PAGES, ru: ruPages, es: esPages };

/** The languages with a URL prefix (/ru/…, /es/…); English, the default, lives at the root. */
export const PREFIXED_LANGS = LANGS.filter((l) => l !== DEFAULT_LANG);

/** hreflang and Open Graph locale of each language. */
export const HREFLANG: Record<Lang, string> = { en: "en", ru: "ru", es: "es" };
export const OG_LOCALE: Record<Lang, string> = { en: "en_US", ru: "ru_RU", es: "es_ES" };

/** The landing also answers at /welcome (ad links; /ru/welcome, /es/welcome too); its canonical is "/" (/ru, /es). */
export const LANDING_ALIASES = ["/", "/welcome"] as const;
export const isLanding = (path: string): boolean => (LANDING_ALIASES as readonly string[]).includes(path);

const isPublic = (path: string): boolean => isLanding(path) || Object.hasOwn(PUBLIC_PAGES, path);

/**
 * A URL path as the app sees it: the public page and its language (/ru/faq → "/faq", ru; /faq → "/faq", en), or for
 * any other path (an app path, an unknown one, /ru/accounts) the path itself and no language of its own.
 */
export function splitLocale(raw: string): { lang: Lang | null; path: string } {
  for (const l of PREFIXED_LANGS) {
    const prefix = `/${l}`;
    if (raw !== prefix && !raw.startsWith(`${prefix}/`)) continue;
    const rest = raw.slice(prefix.length) || "/";
    return isPublic(rest) ? { lang: l, path: rest } : { lang: null, path: raw };
  }
  return { lang: isPublic(raw) ? DEFAULT_LANG : null, path: raw };
}

/** The URL of a public page in a language: ("/faq", ru) → "/ru/faq", ("/", ru) → "/ru", ("/faq", en) → "/faq". */
export function localePath(path: string, lang: Lang): string {
  if (lang === DEFAULT_LANG) return path;
  return path === "/" ? `/${lang}` : `/${lang}${path}`;
}

/** An in-app link in the current language: a public page gets the language's prefix, anything else stays as it is. */
export function localizeHref(href: string, lang: Lang): string {
  const at = href.search(/[?#]/);
  const path = at < 0 ? href : href.slice(0, at);
  return isPublic(path) ? localePath(path, lang) + (at < 0 ? "" : href.slice(at)) : href;
}

/**
 * App paths (exact or with sub-paths): the SPA shell, never indexed. The first group needs a signed-in user — a guest is
 * sent to sign-in — the second is open to guests.
 */
export const SIGNED_IN_PATHS = ["accounts", "terminal", "certificate", "settings", "coach", "support", "buy", "checkout", "orders", "kyc", "payouts", "balance", "affiliate", "security", "admin"] as const;
export const GUEST_APP_PATHS = ["login", "register", "mfa", "forgot-password", "reset-password", "verify-email"] as const;

const under = (path: string, names: readonly string[]) => names.some((n) => path === `/${n}` || path.startsWith(`/${n}/`));

/** A page that needs a signed-in user. */
export const needsSignIn = (path: string): boolean => under(path, SIGNED_IN_PATHS);

/** An app path (Caddy serves the SPA shell with noindex): the cabinet, sign-in, a program version's rules page. */
export const isAppPath = (path: string): boolean => needsSignIn(path) || under(path, GUEST_APP_PATHS) || path.startsWith("/programs/");

export type LocalMeta = PageMeta & { lang: Lang; canonical: string; alternates: Record<Lang, string> };

/** The meta of a public page (the landing for its aliases) in a language, null for any other path. */
export function pageMeta(path: string, lang: Lang = DEFAULT_LANG): LocalMeta | null {
  const key = (isLanding(path) ? "/" : path) as PublicPath;
  const meta = PAGE_METAS[lang][key];
  if (!meta) return null;
  const alternates = Object.fromEntries(LANGS.map((l) => [l, `${SITE}${localePath(key, l)}`])) as Record<Lang, string>;
  return { ...meta, lang, canonical: alternates[lang], alternates };
}
